import {find as _find, get as _get, isEmpty as _isEmpty, union as _union, isEqual as _isEqual} from 'lodash';
import moment from 'moment';

import {alertTemplatesList} from '@graphql/eventContent/query';
import DefaultForm from '@appComponents/DefaultForm';
import {EVENT_CONTENT_BOOKED_BY_CLIENT, EVENT_CONTENT_OFFERED_BY_CLIENT} from '@constants/variables';
import {convertToInt} from '@utils/helpers';
import {WIZARD_TABS_VALIDATION_TEXT} from '@constants/messages';
import {showEmailPreview} from '@utils/forms';
import {
    getClientsFromDistributions,
    setDistributions,
    parseDistributionsForList,
    hasNewRecipientTypeClients,
} from '@modules/eventContentAlert/utils/helpers';

class EventContentAlertForm extends DefaultForm {
    prepareDataForSubmit = (data) => {
        return {
            additional_recipients: data.additional_recipients || [],
            eventContentId: convertToInt(this.props.eventContentId),
            template: convertToInt(data.template),
            message: data.message,
            recipients: this.state.recipients,
        };
    };

    setRecipientType = (startDatetime) => {
        let preselectTypes = [EVENT_CONTENT_BOOKED_BY_CLIENT];

        if (moment(startDatetime).isAfter(moment().add('30', 'm'))) {
            preselectTypes.push(EVENT_CONTENT_OFFERED_BY_CLIENT);
        }

        this.setField('recipient_type', {
            defaultValue: preselectTypes,
        });
    };

    getSelectedClients = (clientsArray) => {
        this.setState(() => ({
            recipients: clientsArray,
        }), this.checkRecipients);

        this.setField('recipients', {
            selected: clientsArray,
        });
    };

    componentDidMount() {
        this.setRecipientType(this.props.eventContentStartDatetime);
        this.setField('recipients', {
            getSelectedClients: this.getSelectedClients,
        });

        this.setOnChangeCallback({
            type: (data) => {
                this.typeOnChangeCallback(data.value);
            },
            template: (data) => {
                this.templateOnChangeCallback(data.value);
            },
            distributions: (data) => {
                this.setState(() => ({
                    distributionsType: data.value,
                }), this.setClientSelection);
            },
            additional_recipients: (data) => {
                this.checkRecipients(data.value);
            },
        });
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);

        if (!_isEqual(this.props, prevProps) && !this.props.GraphQLOptionsData.loading) {
            this.tryPrefillTypeAndTemplate(prevProps);
            this.trySetClientSelectionOnFormChange(prevProps);
            this.tryInitDistributions();
            this.tryInitClientDistributions();
            this.tryClearRecipientsOnReset(prevProps);
        }
    }

    tryPrefillTypeAndTemplate(prevProps) {
        const {GraphQLOptionsData, preFillData} = this.props;

        if (
            prevProps.GraphQLOptionsData.loading &&
            !GraphQLOptionsData.loading &&
            preFillData
        ) {
            this.setField('type', {defaultValue: preFillData.type.toString()});
            this.typeOnChangeCallback(preFillData.type, true);
        }
    }

    trySetClientSelectionOnFormChange(prevProps) {
        const {formValues} = this.props;

        const distributionsChanged = !_isEqual(formValues.distributions, prevProps.formValues.distributions);
        const recipientTypeChanged = !_isEqual(formValues.recipient_type, prevProps.formValues.recipient_type);

        const shouldUpdateRecipients =
            hasNewRecipientTypeClients(prevProps.formValues.recipient_type,
                formValues.recipient_type,
                this.state.bookedClients,
                this.state.offeredClients) ||
            distributionsChanged ||
            recipientTypeChanged;

        if (
            0 < formValues.distributions.length &&
            0 < formValues.recipient_type.length &&
            !this.state.clientsSelected
        ) {
            this.setClientSelection(this.props);
            this.setState({clientsSelected: true});
        } else if (shouldUpdateRecipients) {
            this.setClientSelection(this.props);
        }
    }

    tryInitDistributions() {
        let distributionsList = _get(this.props, 'GraphQLOptionsData.distributions', null);

        if (!distributionsList) return;

        const distributions = _union(
            distributionsList.map(d => convertToInt(d.product.id))
        );

        this.setField('distributions', {
            defaultValue: distributions,
            options: parseDistributionsForList(distributionsList),
        });
        this.setState(() => ({
            distributionsSelected: true,
        }));
    }

    tryInitClientDistributions() {
        if (this.state.distributionObjectCreated) return;

        const clientsList = _get(this.props, 'GraphQLOptionsData.clientListForEventContentAlerts');

        if (!clientsList) return;

        const booked = setDistributions(clientsList, {}, 'bookings');
        const offered = setDistributions(clientsList, {}, 'offerings');

        this.setState({
            distributionObjectCreated: true,
            clientDistributionsObject: {
                [EVENT_CONTENT_BOOKED_BY_CLIENT]: booked,
                [EVENT_CONTENT_OFFERED_BY_CLIENT]: offered,
            },
        });
    }

    tryClearRecipientsOnReset(prevProps) {
        const {formValues} = this.props;

        if (
            _isEmpty(formValues.recipient_type) &&
            formValues.recipient_type !== prevProps.formValues.recipient_type
        ) {
            this.setState({
                recipients: [],
                defaultForm_disableSubmit: true,
            });
            this.setField('recipients', {selected: []});
        }
    }

    typeOnChangeCallback = (value, preFillTemplate = false) => {
        this.setField('template', {loading: true, disabled: true});
        this.setField('message', {defaultValue: '', disabled: true});

        this.props.client.query({
            query: alertTemplatesList,
            variables: {
                alertTemplateType: value,
            },
        }).then((result) => {
            let templateProps = {
                disabled: false,
                loading: false,
                options: result.data.eventContentAlertTemplates,
            };

            if (preFillTemplate) {
                Object.assign(templateProps, {defaultValue: this.props.preFillData.template.toString()});
            }

            this.setField('template', templateProps);

            this.setState(() => ({
                templates: result.data.eventContentAlertTemplates,
            }), () => {
                if (preFillTemplate) {
                    this.templateOnChangeCallback(this.props.preFillData.template.toString());
                }
            });
        });
    };

    templateOnChangeCallback = (value) => {
        if (value) {
            let template = _find(this.state.templates, {id: value});

            this.setField('message', {disabled: false, defaultValue: template.message});
        } else {
            this.setField('message', {disabled: true});
        }
    };

    setClientSelection = (props = this.props) => {
        const {clientsSelection, bookedClients, offeredClients} = getClientsFromDistributions(
            props.formValues.distributions,
            props.formValues.recipient_type,
            this.state.clientDistributionsObject
        );

        const validatedClientsSelection = clientsSelection.filter(clientId =>
            props.GraphQLOptionsData.clients.some(client => client.id === clientId)
        );

        this.setField('recipients', {selected: validatedClientsSelection});
        this.setState(() => ({
            recipients: validatedClientsSelection,
            bookedClients: bookedClients,
            offeredClients: offeredClients,
            defaultForm_disableSubmit: _isEmpty(validatedClientsSelection),
        }));
    };

    renderSaveButton = () => {
        const additionalRecipients = _get(this.props, 'formValues.additional_recipients', []);

        return super.renderSaveButton({
            disabled: 0 === this.state.recipients?.length && _isEmpty(additionalRecipients),
            content: 'Preview',
            icon: null,
        });
    };

    checkRecipients = (additional_recipients = _get(this.props, 'formValues.additional_recipients', [])) => {
        let isFormSubmitDisabled = (
            0 === additional_recipients.length
            && 0 === _get(this.state, 'recipients', []).length
        );

        this.setState(() => ({
            defaultForm_disableSubmit: isFormSubmitDisabled,
        }));
    };

    onFormSubmit(formValues) {
        if (!_isEmpty(this.props.stepsMethods) && !this.props.stepsMethods.validateForms()) {
            this.setFormMessage(WIZARD_TABS_VALIDATION_TEXT(
                _get(this.props, 'Model.label', _get(this.props, 'Model.formName', ''))
                    .toLocaleLowerCase()
            ));
        }

        this.setState(() => ({
            defaultForm_errorShowed: false,
        }));

        const data = this.prepareDataForSubmit(formValues);

        this.props.MessageBox.removeMessage(this.state.defaultForm_formValidationMessageBoxName);

        showEmailPreview(this.props, data);
    }
}

export default EventContentAlertForm;
